import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/OrdersService';
import { Order } from '../types';

export const useOrder = (orderId?: string) => {
    const queryClient = useQueryClient();

    // FETCH
    const orderQuery = useQuery({
        queryKey: ['order', orderId],
        queryFn: () => fetch(orderId!),
        enabled: !!orderId
    });

    // CREATE MUTATION
    const createMutation = useMutation({
        mutationFn: (values: Order) => {
            const { createdByUsername: _createdByUsername, created: _created, updated: _updated, ...rest } = values;
            return add(rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['orders'] });
        }
    });

    // UPDATE MUTATION
    const updateMutation = useMutation({
        mutationFn: (values: Order) => {
            if (!orderId) throw new Error('orderId is required parameter');
            const { createdByUsername: _createdByUsername, created: _created, updated: _updated, ...rest } = values;
            return update(orderId, rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['orders'] });
            queryClient.invalidateQueries({ queryKey: ['order', orderId] });
        }
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!orderId) throw new Error('orderId is required parameter');
            return deleteRecord(orderId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['orders'] });
        }
    });

    return {
        record: orderQuery.data,
        loading: orderQuery.isLoading,
        fetchRecord: orderQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};